import { useCallback, useRef } from 'react';

import { pickRecorderMimeType } from '@/lib/utils/audioFormats';
import type { RecorderInterruptReason } from '@/lib/utils/interruptionEvents';

export interface UseAudioRecorderApi {
  cancel: () => void;
  pause: () => void;
  reacquireMic: () => Promise<void>;
  resume: () => void;
  start: () => Promise<void>;
  stop: () => Promise<{ blob: Blob; mimeType: string }>;
}

export interface UseAudioRecorderProps {
  onError: (err: unknown) => void;
  onInterrupt: (reason: RecorderInterruptReason) => void;
}

const TIMESLICE_MS = 100;

export function useAudioRecorder({ onError, onInterrupt }: UseAudioRecorderProps): UseAudioRecorderApi {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  // Cached at start() so re-acquisition after interruption keeps the same
  // container format across chunks.
  const mimeTypeRef = useRef<string>('');
  // Holds the reject() callback of the in-flight stop() Promise so cancel()
  // can break callers out of an awaited stop instead of leaving the Promise
  // pending forever once we detach onstop.
  const pendingStopRejectRef = useRef<((reason: unknown) => void) | null>(null);
  // Holds an abort callback while reacquireMic() is mid-flight. cancel()
  // invokes it to (a) flip an internal flag so reacquireMic bails before
  // attaching a fresh recorder to refs cancel just cleared, and (b) wake the
  // onstop await whose resolver cancel itself nulled out.
  const reacquireAbortRef = useRef<(() => void) | null>(null);

  const attachRecorder = useCallback(
    (stream: MediaStream) => {
      const mimeType = mimeTypeRef.current || pickRecorderMimeType();
      if (!mimeType) {
        stream.getTracks().forEach((t) => t.stop());
        throw new Error('No supported audio format found in your browser');
      }
      mimeTypeRef.current = mimeType;

      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onerror = (err) => {
        onError(err);
        onInterrupt('recorder-error');
      };
      stream.getAudioTracks().forEach((track) => {
        // { once: true } prevents a stale ended event from re-triggering
        // onInterrupt after the track has been replaced by reacquireMic.
        track.addEventListener('ended', () => onInterrupt('track-ended'), { once: true });
      });

      recorder.start(TIMESLICE_MS);
      recorderRef.current = recorder;
      streamRef.current = stream;
    },
    [onError, onInterrupt],
  );

  const start = useCallback(async () => {
    chunksRef.current = [];
    mimeTypeRef.current = '';
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    attachRecorder(stream);
  }, [attachRecorder]);

  const pause = useCallback(() => {
    const r = recorderRef.current;
    if (r && r.state === 'recording') r.pause();
  }, []);

  const resume = useCallback(() => {
    const r = recorderRef.current;
    if (r && r.state === 'paused') r.resume();
  }, []);

  const buildBlob = useCallback((): { blob: Blob; mimeType: string } => {
    const mimeType = mimeTypeRef.current || 'audio/webm';
    return { mimeType, blob: new Blob(chunksRef.current, { type: mimeType }) };
  }, []);

  const stop = useCallback(async (): Promise<{ blob: Blob; mimeType: string }> => {
    const r = recorderRef.current;
    if (!r || r.state === 'inactive') {
      const result = buildBlob();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      recorderRef.current = null;
      chunksRef.current = [];
      mimeTypeRef.current = '';
      pendingStopRejectRef.current = null;
      return result;
    }
    return new Promise<{ blob: Blob; mimeType: string }>((resolve, reject) => {
      pendingStopRejectRef.current = reject;
      r.onstop = () => {
        const result = buildBlob();
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        recorderRef.current = null;
        chunksRef.current = [];
        mimeTypeRef.current = '';
        resolve(result);
        pendingStopRejectRef.current = null;
      };
      try {
        r.requestData();
      } catch {
        // Some MediaRecorder implementations throw if the recorder is mid-
        // transition. Safe to ignore — stop() below will still flush.
      }
      r.stop();
    });
  }, [buildBlob]);

  // Discards in-flight recording and releases the mic. If a stop() Promise
  // is in flight, it will be rejected with Error('cancelled') so callers can
  // detect cancellation instead of awaiting forever (we detach onstop below,
  // which would otherwise leave the Promise pending).
  const cancel = useCallback(() => {
    if (pendingStopRejectRef.current) {
      pendingStopRejectRef.current(new Error('cancelled'));
      pendingStopRejectRef.current = null;
    }
    // Wake an in-flight reacquireMic so it can short-circuit instead of
    // attaching a fresh recorder to the refs we're about to clear (which
    // would leak a live mic stream).
    if (reacquireAbortRef.current) {
      const abort = reacquireAbortRef.current;
      reacquireAbortRef.current = null;
      abort();
    }
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') {
      r.onstop = null;
      r.stop();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
    mimeTypeRef.current = '';
  }, []);

  const reacquireMic = useCallback(async () => {
    // User-initiated stop() is in flight. Respect their intent — do not
    // hijack the recorder mid-stop. The provider above us will see the
    // stop complete normally and transition to 'stopped' state.
    if (pendingStopRejectRef.current) {
      return;
    }

    let aborted = false;
    const dying = recorderRef.current;
    const dyingStream = streamRef.current;

    if (dying && dying.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        // Wire the abort hook to both flip the flag and wake this await.
        // cancel() nulls dying.onstop on its way out, which would leave the
        // resolver permanently dangling without this escape.
        reacquireAbortRef.current = () => {
          aborted = true;
          resolve();
        };
        dying.onstop = () => resolve();
        try {
          dying.requestData();
        } catch {
          // Some MediaRecorder implementations throw if mid-transition.
          // Safe to ignore — stop() below will still flush.
        }
        dying.stop();
      });
      reacquireAbortRef.current = null;
      if (aborted) return;
    }
    dyingStream?.getTracks().forEach((t) => t.stop());
    recorderRef.current = null;
    streamRef.current = null;

    // Re-arm the abort hook for the getUserMedia phase. getUserMedia itself
    // isn't AbortSignal-aware, so we can't cancel the request — but we can
    // detect that cancel ran while we were awaiting it and release the
    // newly-acquired stream instead of attaching a recorder to it.
    reacquireAbortRef.current = () => {
      aborted = true;
    };
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } finally {
      reacquireAbortRef.current = null;
    }
    if (aborted) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    attachRecorder(stream); // reuses cached mimeTypeRef.current
  }, [attachRecorder]);

  return { stop, pause, start, cancel, resume, reacquireMic };
}
